import { morphSection } from '@theme/section-renderer';

function getCartSectionId() {
  return document.querySelector('cart-items-component')?.dataset?.sectionId;
}

async function cartAddItem(variantId, quantity = 1) {
  const sectionId = getCartSectionId();
  const body = JSON.stringify({
    id: variantId,
    quantity,
    sections: sectionId,
    sections_url: window.location.pathname,
  });

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body,
  });

  if (!response.ok) throw new Error('Cart add failed');
  const data = await response.json();

  if (sectionId && data.sections?.[sectionId]) {
    morphSection(sectionId, data.sections[sectionId]);
  }
  document.dispatchEvent(new CustomEvent('cart:update', {
    bubbles: true,
    detail: { resource: data, data: { itemCount: data.item_count } },
  }));
  return data;
}

async function cartRemoveItem(variantId) {
  const sectionId = getCartSectionId();
  const body = JSON.stringify({
    updates: { [variantId]: 0 },
    sections: sectionId,
    sections_url: window.location.pathname,
  });

  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body,
  });

  if (!response.ok) throw new Error('Cart update failed');
  const data = await response.json();

  if (sectionId && data.sections?.[sectionId]) {
    morphSection(sectionId, data.sections[sectionId]);
  }
  document.dispatchEvent(new CustomEvent('cart:update', {
    bubbles: true,
    detail: { resource: data, data: { itemCount: data.item_count } },
  }));
  return data;
}

/**
 * Syncs milestone gifts: auto-adds when threshold reached, removes when dropped below.
 * Called after every cart operation.
 */
async function syncMilestoneGifts() {
  const milestones = window.cartMilestones || [];
  const giftMilestones = milestones.filter(m => m.giftVariantId && m.giftVariantId > 0);
  if (giftMilestones.length === 0) return;

  const cartResp = await fetch('/cart.js');
  const cart = await cartResp.json();
  const cartTotal = cart.total_price;
  const cartVariantIds = cart.items.map(i => i.variant_id);

  for (const milestone of giftMilestones) {
    const reached = cartTotal >= milestone.amount;
    const inCart = cartVariantIds.includes(milestone.giftVariantId);

    if (reached && !inCart) {
      await cartAddItem(milestone.giftVariantId, 1);
    } else if (!reached && inCart) {
      await cartRemoveItem(milestone.giftVariantId);
    }
  }
}

/**
 * Syncs "gift with purchase" by collection: if the cart contains any product
 * from a trigger collection, auto-adds the configured gift variant (exactly
 * one per order, regardless of how many qualifying products/quantity are in
 * the cart). Removes the gift automatically if no qualifying product remains.
 * Called after every cart operation, same as syncMilestoneGifts.
 */
async function syncCollectionGifts() {
  const rules = window.cartCollectionGifts || [];
  const giftRules = rules.filter(r => r.giftVariantId && r.giftVariantId > 0 && Array.isArray(r.triggerProductIds) && r.triggerProductIds.length > 0);
  if (giftRules.length === 0) return;

  const cartResp = await fetch('/cart.js');
  const cart = await cartResp.json();
  const cartProductIds = cart.items.map(i => i.product_id);
  const cartVariantIds = cart.items.map(i => i.variant_id);

  for (const rule of giftRules) {
    const qualifies = rule.triggerProductIds.some(id => cartProductIds.includes(id));
    const inCart = cartVariantIds.includes(rule.giftVariantId);

    if (qualifies && !inCart) {
      await cartAddItem(rule.giftVariantId, 1);
    } else if (!qualifies && inCart) {
      await cartRemoveItem(rule.giftVariantId);
    }
  }
}

/** Runs every automatic-gift sync (milestones + collection gifts) */
async function syncAutoGifts() {
  await syncMilestoneGifts();
  await syncCollectionGifts();
}

/** "Agregar" button on upsell cards */
window.customCartUpsellAdd = async function (button) {
  const variantId = parseInt(button.dataset.variantId, 10);
  if (!variantId) return;

  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = '...';

  try {
    await cartAddItem(variantId);
    await syncAutoGifts();
  } catch (err) {
    console.error('Upsell add failed:', err);
    button.disabled = false;
    button.textContent = originalText;
  }
};

/** Gift box checkbox */
window.customCartGiftToggle = async function (checkbox) {
  const variantId = parseInt(checkbox.dataset.variantId, 10);
  const isChecked = checkbox.checked;
  if (!variantId) return;

  checkbox.disabled = true;

  try {
    if (isChecked) {
      await cartAddItem(variantId);
    } else {
      await cartRemoveItem(variantId);
    }
    await syncAutoGifts();
  } catch (err) {
    console.error('Gift toggle failed:', err);
    checkbox.checked = !isChecked;
    checkbox.disabled = false;
  }
};

/** Listen to cart:update events fired by the theme to sync automatic gifts */
document.addEventListener('cart:update', async (event) => {
  const source = event.detail?.data?.source;
  // Avoid infinite loop: don't re-sync when we triggered the update ourselves
  if (source === 'custom-cart-milestone-sync') return;
  await syncAutoGifts();
});
